import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multiselect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import {
  PoundSterling,
  TrendingUp,
  AlertTriangle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useTableSort } from '@/hooks/useTableSort';

import { mockCompanyData } from '@/components/analytics/company-details-sidebar';

const ITEMS_PER_PAGE = 7;

type TrendPeriod = 'latest' | '7days' | '30days' | '60days' | '90days';

export default function MultiCompanyDashboard() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<TrendPeriod>('latest');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('all');

  // Get unique currencies from company data
  const availableCurrencies = useMemo(() => {
    const currencies = Array.from(new Set(mockCompanyData.map(x => x.currency))).sort((a, b) => a.localeCompare(b));
    return currencies;
  }, []);

  // Filter companies by currency first (before other filters)
  const currencyFilteredData = useMemo(() => {
    if (selectedCurrency === 'all') {
      return mockCompanyData;
    }
    return mockCompanyData.filter(company => company.currency === selectedCurrency);
  }, [selectedCurrency]);

  // Calculate aggregated metrics based on currency-filtered data
  const totalOutstanding = currencyFilteredData.reduce((sum, company) => sum + company.totalAR, 0);
  const totalOverdue = currencyFilteredData.reduce((sum, company) => sum + company.overdue, 0);
  const avgDSO = currencyFilteredData.length > 0 
    ? Math.round(currencyFilteredData.reduce((sum, company) => sum + company.dso, 0) / currencyFilteredData.length)
    : 0;

  // Find most at-risk company from currency-filtered data
  const mostAtRiskCompany = currencyFilteredData.length > 0
    ? currencyFilteredData.reduce((max, company) => 
        company.overduePercent > max.overduePercent ? company : max
      , currencyFilteredData[0])
    : null;

  // Company options for multi-select
  const companyOptions = mockCompanyData.map(company => ({
    value: company.id,
    label: company.name,
  }));

  // Filter data based on currency, selected companies, and search query
  const filteredData = useMemo(() => {
    let data = currencyFilteredData;

    // Filter by selected companies
    if (selectedCompanyIds.length > 0) {
      data = data.filter(company => selectedCompanyIds.includes(company.id));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter(company => 
        company.name.toLowerCase().includes(query)
      );
    }

    return data;
  }, [currencyFilteredData, selectedCompanyIds, searchQuery]);

  // Sorting
  const { sortedData, requestSort, getSortDirection } = useTableSort(filteredData);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [selectedCurrency, selectedCompanyIds, searchQuery]);

  const formatCurrency = (value: number, currency?: string) => {
    // If currency is provided, use it; otherwise fall back to selectedCurrency
    const currencyCode = currency || (selectedCurrency === 'all' ? 'GBP' : selectedCurrency);
    
    // Use appropriate locale for each currency
    const locale = currencyCode === 'USD' ? 'en-US' : 'en-GB';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getOverdueColor = (percent: number) => {
    if (percent >= 30) return 'text-red-600';
    if (percent >= 20) return 'text-orange-600';
    return 'text-green-600';
  };

  const getDSOColor = (dso: number) => {
    if (dso >= 60) return 'text-red-600';
    if (dso >= 45) return 'text-orange-600';
    return 'text-green-600';
  };

  const getCEIColor = (cei: number) => {
    if (cei >= 90) return 'text-green-600';
    if (cei >= 75) return 'text-orange-600';
    return 'text-red-600';
  };

  const getTrendPeriodLabel = (period: TrendPeriod) => {
    const labels: Record<TrendPeriod, string> = {
      latest: 'Latest',
      '7days': '7 Days',
      '30days': '30 Days',
      '60days': '60 Days',
      '90days': '90 Days',
    };
    return labels[period];
  };

  const getExportData = () => filteredData.map(company => ({
    Company: company.name,
    Currency: company.currency,
    'Total AR': formatCurrency(company.totalAR, company.currency),
    'Overdue': formatCurrency(company.overdue, company.currency),
    'Overdue %': `${company.overduePercent.toFixed(1)}%`,
    'DSO': company.dso,
    'CEI': `${company.cei}%`,
    'Avg Days to Pay': company.avgDaysToPay,
    'AR Trend': company.arTrend,
    'Overdue Trend': company.overdueTrend,
    'DSO Trend': company.dsoTrend,
    'CEI Trend': company.ceiTrend,
    'Payment Speed': company.paymentSpeed,
  }));

  const exportToCSV = () => {
    const dataToExport = getExportData();
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row =>
        headers.map(header => {
          const value = row[header as keyof typeof row];
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `company-performance-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToXLS = () => {
    const dataToExport = getExportData();
    const headers = Object.keys(dataToExport[0] || {});

    // Build an HTML table which Excel can open natively as .xls
    const tableRows = [
      '<tr>' + headers.map(x => '<th>' + x + '</th>').join('') + '</tr>',
      ...dataToExport.map(row =>
        '<tr>' + headers.map(x => '<td>' + String(row[x as keyof typeof row]) + '</td>').join('') + '</tr>'
      )
    ].join('');

    const xlsContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="UTF-8"/></head>
        <body><table>${tableRows}</table></body>
      </html>`;

    const blob = new Blob([xlsContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `company-performance-${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Multi-Company Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Consolidated view across all companies in your portfolio
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Currency:</span>
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Currencies</SelectItem>
              {availableCurrencies.map((currency) => (
                <SelectItem key={currency} value={currency}>
                  {currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Top Section - Key Performance Metrics */}
      {selectedCurrency === 'all' ? (
        <Card className="p-6 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-amber-900">Multi-Currency View</p>
              <p className="text-sm text-amber-800">
                Companies use different currencies. Please select a specific currency to view aggregated metrics, 
                or use the table below to compare individual company performance.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Outstanding */}
          <NavigationMetricCardAlt
            title="TOTAL OUTSTANDING"
            value={formatCurrency(totalOutstanding)}
            secondaryValue="+8% vs last month"
            icon={PoundSterling}
            variant="info"
            onClick={() => {}}
            showButton={false}
          />

          {/* Total Overdue */}
          <NavigationMetricCardAlt
            title="TOTAL OVERDUE"
            value={formatCurrency(totalOverdue)}
            secondaryValue="+12% vs last month"
            icon={TrendingUp}
            variant="info"
            onClick={() => {}}
            showButton={false}
          />

          {/* Most At-Risk Company */}
          <NavigationMetricCardAlt
            title="MOST AT-RISK COMPANY"
            value={mostAtRiskCompany?.name || 'N/A'}
            secondaryValue={mostAtRiskCompany ? `${mostAtRiskCompany.overduePercent.toFixed(1)}% overdue` : 'No companies'}
            icon={AlertTriangle}
            variant="info"
            onClick={() => mostAtRiskCompany && navigate(`/analytics-reporting/multi-company/company/${mostAtRiskCompany.id}`)}
            showButton={!!mostAtRiskCompany}
          />

          {/* Avg DSO */}
          <NavigationMetricCardAlt
            title="AVG DSO (LAST 30 DAYS)"
            value={`${avgDSO} days`}
            secondaryValue="-2 days improvement"
            icon={Calendar}
            variant="info"
            onClick={() => {}}
            showButton={false}
          />
        </div>
      )}

      {/* Bottom Section - Company Performance Comparison Table */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Table Header with Search and Filters */}
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold whitespace-nowrap">Company Performance Comparison</h2>
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-1 max-w-xs">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <MultiSelect
                  options={companyOptions}
                  placeholder="Filter by companies..."
                  emptyMessage="No companies found"
                  value={selectedCompanyIds}
                  onValueChange={setSelectedCompanyIds}
                  maxDisplay={2}
                />
              </div>
              <Select value={viewMode} onValueChange={(v) => setViewMode(v as TrendPeriod)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest</SelectItem>
                  <SelectItem value="7days">7 Days</SelectItem>
                  <SelectItem value="30days">30 Days</SelectItem>
                  <SelectItem value="60days">60 Days</SelectItem>
                  <SelectItem value="90days">90 Days</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    title="Export"
                    disabled={filteredData.length === 0}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToXLS}>
                    Export to XLS
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToCSV}>
                    Export to CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <SortableTableHead
                      sortKey="name"
                      currentSortDirection={getSortDirection('name')}
                      onSort={() => requestSort('name')}
                      align="left"
                    >
                      Company
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="currency"
                      currentSortDirection={getSortDirection('currency')}
                      onSort={() => requestSort('currency')}
                      align="left"
                    >
                      Currency
                    </SortableTableHead>
                    {viewMode === 'latest' ? (
                      <>
                        <SortableTableHead
                          sortKey="totalAR"
                          currentSortDirection={getSortDirection('totalAR')}
                          onSort={() => requestSort('totalAR')}
                          align="right"
                        >
                          Total AR
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="overdue"
                          currentSortDirection={getSortDirection('overdue')}
                          onSort={() => requestSort('overdue')}
                          align="right"
                        >
                          Overdue
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="overduePercent"
                          currentSortDirection={getSortDirection('overduePercent')}
                          onSort={() => requestSort('overduePercent')}
                          align="right"
                        >
                          Overdue %
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="dso"
                          currentSortDirection={getSortDirection('dso')}
                          onSort={() => requestSort('dso')}
                          align="right"
                        >
                          DSO
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="cei"
                          currentSortDirection={getSortDirection('cei')}
                          onSort={() => requestSort('cei')}
                          align="right"
                        >
                          CEI
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="avgDaysToPay"
                          currentSortDirection={getSortDirection('avgDaysToPay')}
                          onSort={() => requestSort('avgDaysToPay')}
                          align="right"
                        >
                          Avg Days to Pay
                        </SortableTableHead>
                      </>
                    ) : (
                      <>
                        <SortableTableHead
                          sortKey="arTrend"
                          currentSortDirection={getSortDirection('arTrend')}
                          onSort={() => requestSort('arTrend')}
                          align="right"
                        >
                          AR Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="overdueTrend"
                          currentSortDirection={getSortDirection('overdueTrend')}
                          onSort={() => requestSort('overdueTrend')}
                          align="right"
                        >
                          Overdue Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="dsoTrend"
                          currentSortDirection={getSortDirection('dsoTrend')}
                          onSort={() => requestSort('dsoTrend')}
                          align="right"
                        >
                          DSO Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="ceiTrend"
                          currentSortDirection={getSortDirection('ceiTrend')}
                          onSort={() => requestSort('ceiTrend')}
                          align="right"
                        >
                          CEI Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="paymentSpeed"
                          currentSortDirection={getSortDirection('paymentSpeed')}
                          onSort={() => requestSort('paymentSpeed')}
                          align="right"
                        >
                          Payment Speed
                        </SortableTableHead>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={viewMode === 'latest' ? 8 : 7} className="p-8 text-center text-muted-foreground">
                        No companies found matching your filters
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((company) => (
                    <tr
                      key={company.id}
                      className="border-t hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/analytics-reporting/multi-company/company/${company.id}`)}
                    >
                      <td className="p-4 font-medium">
                        <button
                          type="button"
                          className="text-primary hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/analytics-reporting/multi-company/company/${company.id}`);
                          }}
                        >
                          {company.name}
                        </button>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="font-mono">
                          {company.currency}
                        </Badge>
                      </td>
                      {viewMode === 'latest' ? (
                        <>
                          <td className="text-right p-4">{formatCurrency(company.totalAR, company.currency)}</td>
                          <td className="text-right p-4">{formatCurrency(company.overdue, company.currency)}</td>
                          <td className={cn("text-right p-4 font-medium", getOverdueColor(company.overduePercent))}>
                            {company.overduePercent.toFixed(1)}%
                          </td>
                          <td className={cn("text-right p-4 font-medium", getDSOColor(company.dso))}>
                            {company.dso}
                          </td>
                          <td className={cn("text-right p-4 font-medium", getCEIColor(company.cei))}>
                            {company.cei}%
                          </td>
                          <td className="text-right p-4">{company.avgDaysToPay}</td>
                        </>
                      ) : (
                        <>
                          <td className="text-right p-4 text-green-600 font-medium">{company.arTrend}</td>
                          <td className="text-right p-4 text-orange-600 font-medium">{company.overdueTrend}</td>
                          <td className="text-right p-4 text-green-600 font-medium">{company.dsoTrend}</td>
                          <td className="text-right p-4 text-green-600 font-medium">{company.ceiTrend}</td>
                          <td className="text-right p-4">
                            <Badge
                              variant={
                                (() => {
                                  if (company.paymentSpeed === 'Improving') return 'default';
                                  if (company.paymentSpeed === 'Declining') return 'destructive';
                                  return 'secondary';
                                })()
                              }
                            >
                              {company.paymentSpeed}
                            </Badge>
                          </td>
                        </>
                      )}
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {sortedData.length === 0 ? (
                'No companies found'
              ) : (
                <>
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} companies
                  {(selectedCurrency !== 'all' || selectedCompanyIds.length > 0 || searchQuery) && (
                    <span className="ml-1">(filtered from {mockCompanyData.length} total)</span>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

    </div>
  );
}
